import { memo, useEffect, useRef } from 'react';
import { createParticleEngine, type ParticleEngine } from './engine.js';
import { PRESETS } from './presets.js';
import { subscribeBurst } from './burst.js';
import { useDeviceTier } from '../useDeviceTier.js';
import { useReducedMotion } from './useReducedMotion.js';

/**
 * `<BurstLayer>` — the single full-screen, fixed canvas that renders every burst
 * fired through the `burst()` bus. Mount it once near the app root (above the UI,
 * `pointer-events:none`). It holds its own engine with no ambient emitters; each
 * bus message becomes a one-shot spawn at the requested normalized position.
 *
 * Reduced motion: renders nothing and ignores the bus.
 */

function BurstLayerImpl() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<ParticleEngine | null>(null);
  const reduced = useReducedMotion();
  const { caps } = useDeviceTier();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || reduced) return;

    const engine = createParticleEngine(canvas, {
      dprCap: caps.dprCap,
      // Bursts get the full budget — they're brief and want to read punchy.
      maxParticles: caps.maxParticles,
    });
    engineRef.current = engine;

    const unsub = subscribeBurst((req) => {
      const preset = PRESETS[req.preset];
      engine.burst(preset, { x: req.x, y: req.y, count: req.count });
    });

    return () => {
      unsub();
      engine.destroy();
      engineRef.current = null;
    };
  }, [reduced, caps.dprCap, caps.maxParticles]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    />
  );
}

export const BurstLayer = memo(BurstLayerImpl);
